<!-- Base64FileField.vue -->
<script setup>
import { ref, computed } from "vue";

const props = defineProps({
  modelValue: {
    type: [String, Object, null],
    default: null, // will emit { name, type, size, base64 } by default
  },
  label: { type: String, default: "Attachment" },
  accept: { type: String, default: "image/png" }, // e.g. "image/*,.pdf"
  returnOnlyBase64: { type: Boolean, default: false }, // set true to emit only base64 string
  maxBytes: { type: Number, default: 20 * 1024 * 1024 }, // 20MB guard
});

const emit = defineEmits(["update:modelValue", "error"]);

const hover = ref(false);
const fileInput = ref(null);
const fileInfo = computed(() => {
  if (!props.modelValue) return null;
  const obj = typeof props.modelValue === "string"
    ? { name: "file", type: "", size: 0, base64: props.modelValue }
    : props.modelValue;
  return obj;
});

function openPicker() {
  fileInput.value?.click();
}

function onDragOver(e) {
  e.preventDefault();
  hover.value = true;
}

function onDragLeave() {
  hover.value = false;
}

function onDrop(e) {
  e.preventDefault();
  hover.value = false;
  const file = e.dataTransfer?.files?.[0];
  if (file) readFile(file);
}

function onPick(e) {
  const file = e.target.files?.[0];
  if (file) readFile(file);
  // reset input so picking the same file again still fires change
  e.target.value = "";
}

function readFile(file) {
  if (file.size > props.maxBytes) {
    emit("error", `File too large (${formatBytes(file.size)}). Max is ${formatBytes(props.maxBytes)}.`);
    return;
  }
  const reader = new FileReader();
  reader.onerror = () => emit("error", "Failed to read file.");
  reader.onload = () => {
    // reader.result is a data URL like: data:<mime>;base64,<data>
    const dataUrl = reader.result || "";
    const base64 = String(dataUrl).split(",")[1] || ""; // strip prefix
    const payload = props.returnOnlyBase64
      ? base64
      : { name: file.name, type: file.type, size: file.size, base64 };
    emit("update:modelValue", payload);
  };
  reader.readAsDataURL(file);
}

function clearFile() {
  emit("update:modelValue", props.returnOnlyBase64 ? "" : null);
}

function formatBytes(bytes) {
  if (bytes === 0) return "0 B";
  const k = 1024, sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
}
</script>

<template>
  <div class="space-y-2">
    <div class="text-body-2 text-medium-emphasis">{{ label }}</div>

    <v-card
      v-if="!fileInfo"
      class="d-flex align-center justify-center pa-6 rounded-xl"
      :class="hover ? 'elevation-8 border-dashed' : 'elevation-2 border-dashed'"
      style="border: 2px dashed var(--v-theme-primary); cursor: pointer;"
      @click="openPicker"
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop"
    >
      <div class="text-center">
        <div class="text-subtitle-1 font-weight-medium mb-1">
          Drag & drop a file here
        </div>
        <div class="text-body-2 text-medium-emphasis">
          or <span class="text-primary font-weight-medium">click to browse</span>
          <span v-if="accept"> — allowed: {{ accept }}</span>
        </div>
      </div>
      <input
        ref="fileInput"
        type="file"
        :accept="accept"
        class="d-none"
        @change="onPick"
      />
    </v-card>

    <v-alert
      v-if="fileInfo"
      type="success"
      variant="tonal"
      class="mt-2"
      density="comfortable"
    >
      <div class="d-flex align-center justify-space-between w-100">
        <div>
          <div class="font-weight-medium">
            {{ fileInfo.name }}
          </div>
          <div class="text-caption text-medium-emphasis">
            {{ fileInfo.type || 'unknown type' }} •
            <span v-if="fileInfo.size">{{ formatBytes(fileInfo.size) }}</span>
          </div>
        </div>
        <div class="d-flex align-center ga-2">
          <v-btn size="small" variant="text" @click.stop="clearFile">Clear</v-btn>
          <v-tooltip text="Copy Base64 to clipboard" location="top">
            <template #activator="{ props: tip }">
              <v-btn
                v-bind="tip"
                size="small"
                icon="mdi-content-copy"
                variant="text"
                @click.stop="navigator.clipboard.writeText(fileInfo.base64 || (modelValue || ''))"
              />
            </template>
          </v-tooltip>
        </div>
      </div>
    </v-alert>
  </div>
</template>

<style scoped>
.border-dashed { border-style: dashed; }
</style>
